// src/composables/useCacheInvalidation.ts
import { useQueryClient } from '@tanstack/vue-query'
import { watch, onUnmounted } from 'vue'
import { useAuth } from '@/composables/useAuth'
import { invalidateFinancialData, invalidateUserData, invalidateAll, clearAllCache } from '@/utils/cacheUtils'

/**
 * Composable for managing cache invalidation across the application
 * Handles automatic invalidation based on user actions, auth state changes, and custom events
 */
export function useCacheInvalidation() {
  const queryClient = useQueryClient()
  const auth = useAuth()

  // Invalidate specific query types
  const invalidateExpenses = () => {
    queryClient.invalidateQueries({ queryKey: ['expenses'] })
    queryClient.invalidateQueries({ queryKey: ['spending-comparison'] })
    queryClient.invalidateQueries({ queryKey: ['tracked-categories'] })
    queryClient.invalidateQueries({ queryKey: ['expense-details'] })
  }

  const invalidateAccounts = () => {
    queryClient.invalidateQueries({ queryKey: ['accounts'] })
    queryClient.invalidateQueries({ queryKey: ['balances'] })
    queryClient.invalidateQueries({ queryKey: ['account-details'] })
  }

  const invalidateCategories = () => {
    queryClient.invalidateQueries({ queryKey: ['categories'] })
    queryClient.invalidateQueries({ queryKey: ['available-categories'] })
    queryClient.invalidateQueries({ queryKey: ['tracked-categories'] })
  }

  const invalidateProjections = () => {
    queryClient.invalidateQueries({ queryKey: ['spending-comparison'] })
    queryClient.invalidateQueries({ queryKey: ['tracked-categories'] })
  }

  const invalidateScopes = () => {
    queryClient.invalidateQueries({ queryKey: ['scopes'] })
    // When scopes change, we need to refetch everything since data is scope-filtered
    invalidateAll(queryClient)
  }

  // Handle authentication state changes
  const handleAuthStateChange = () => {
    watch(auth.isAuthenticated, (isAuth, wasAuth) => {
      if (wasAuth === true && isAuth === false) {
        clearAllCache()
        queryClient.clear()
      } else if (wasAuth === false && isAuth === true) {
        invalidateAll(queryClient)
      }
    })
  }

  // Handle scope filter changes
  const handleScopeFilterChange = (globalSelectedScopes: any) => {
    watch(globalSelectedScopes, (newScopes, oldScopes) => {
      if (JSON.stringify(newScopes) !== JSON.stringify(oldScopes)) {
        invalidateExpenses()
        invalidateAccounts()
        invalidateProjections()
      }
    })
  }

  // Listen for custom events that indicate data changes
  const setupEventListeners = () => {
    // Expense-related events
    const handleExpenseUpdate = () => {
      invalidateExpenses()
      invalidateProjections()
    }

    const handleAccountUpdate = () => {
      invalidateAccounts()
    }

    const handleCategoryUpdate = () => {
      invalidateCategories()
    }

    const handleScopeUpdate = () => {
      invalidateScopes()
    }

    const handlePlaidSync = () => {
      invalidateFinancialData(queryClient)
    }

    // Register event listeners
    window.addEventListener('expenseUpdated', handleExpenseUpdate)
    window.addEventListener('expenseCreated', handleExpenseUpdate)
    window.addEventListener('expenseDeleted', handleExpenseUpdate)
    window.addEventListener('expenseBulkUpdated', handleExpenseUpdate)
    
    window.addEventListener('accountUpdated', handleAccountUpdate)
    window.addEventListener('accountConnected', handleAccountUpdate)
    window.addEventListener('accountDisconnected', handleAccountUpdate)
    
    window.addEventListener('categoryUpdated', handleCategoryUpdate)
    window.addEventListener('categoryTargetUpdated', handleCategoryUpdate)
    
    window.addEventListener('scopeUpdated', handleScopeUpdate)
    window.addEventListener('scopeChanged', handleScopeUpdate)
    
    window.addEventListener('plaidSyncCompleted', handlePlaidSync)
    window.addEventListener('plaidItemUpdated', handlePlaidSync)

    // Handle browser online/offline events
    const handleOnline = () => {
      queryClient.refetchQueries({
        stale: true,
        type: 'active'
      })
    }

    const handleOffline = () => {
      queryClient.setQueriesData({ predicate: () => true }, (data) => data)
    }

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    // Focus refetch for stale data when user returns to tab
    const handleFocus = () => {
      // Use cached data on tab focus - no automatic refetch
    }

    window.addEventListener('focus', handleFocus)

    // Cleanup function
    return () => {
      window.removeEventListener('expenseUpdated', handleExpenseUpdate)
      window.removeEventListener('expenseCreated', handleExpenseUpdate)
      window.removeEventListener('expenseDeleted', handleExpenseUpdate)
      window.removeEventListener('expenseBulkUpdated', handleExpenseUpdate)
      
      window.removeEventListener('accountUpdated', handleAccountUpdate)
      window.removeEventListener('accountConnected', handleAccountUpdate)
      window.removeEventListener('accountDisconnected', handleAccountUpdate)
      
      window.removeEventListener('categoryUpdated', handleCategoryUpdate)
      window.removeEventListener('categoryTargetUpdated', handleCategoryUpdate)
      
      window.removeEventListener('scopeUpdated', handleScopeUpdate)
      window.removeEventListener('scopeChanged', handleScopeUpdate)
      
      window.removeEventListener('plaidSyncCompleted', handlePlaidSync)
      window.removeEventListener('plaidItemUpdated', handlePlaidSync)
      
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
      window.removeEventListener('focus', handleFocus)
    }
  }

  // Handle storage events (user logged out in another tab)
  const handleStorageChange = () => {
    const handleStorage = (e: StorageEvent) => {
      if (e.key?.includes('auth') && e.newValue === null) {
        clearAllCache()
        queryClient.clear()
      }

      if (e.key === 'app-cache-version' && e.newValue !== e.oldValue) {
        clearAllCache()
        queryClient.clear()
      }
    }

    window.addEventListener('storage', handleStorage)

    return () => {
      window.removeEventListener('storage', handleStorage)
    }
  }

  // Background refresh for long-running tabs
  const setupBackgroundRefresh = () => {
    // Refresh every 6 hours for long-running tabs (half of our 12-hour cache time)
    const BACKGROUND_REFRESH_INTERVAL = 6 * 60 * 60 * 1000 // 6 hours

    const interval = setInterval(() => {
      if (navigator.onLine && document.hasFocus()) {
        queryClient.refetchQueries({
          stale: true,
          type: 'active'
        })
      }
    }, BACKGROUND_REFRESH_INTERVAL)

    return () => {
      clearInterval(interval)
    }
  }

  // Initialize all invalidation strategies
  const initialize = (globalSelectedScopes?: any) => {
    handleAuthStateChange()
    
    if (globalSelectedScopes) {
      handleScopeFilterChange(globalSelectedScopes)
    }
    
    const cleanupEvents = setupEventListeners()
    const cleanupStorage = handleStorageChange()
    const cleanupBackgroundRefresh = setupBackgroundRefresh()

    // Return cleanup function
    onUnmounted(() => {
      cleanupEvents()
      cleanupStorage()
      cleanupBackgroundRefresh()
    })
  }

  return {
    // Individual invalidation functions
    invalidateExpenses,
    invalidateAccounts,
    invalidateCategories,
    invalidateProjections,
    invalidateScopes,
    
    // Bulk invalidation functions
    invalidateFinancialData: () => invalidateFinancialData(queryClient),
    invalidateUserData: () => invalidateUserData(queryClient),
    invalidateAll: () => invalidateAll(queryClient),
    clearAllCache,
    
    // Setup function
    initialize,
    
    // Direct access to query client for custom invalidation
    queryClient,
  }
}

// Utility function to emit cache invalidation events
export function emitCacheInvalidationEvent(eventType: string, data?: any) {
  window.dispatchEvent(new CustomEvent(eventType, { detail: data }))
}

// Predefined events
export const CACHE_EVENTS = {
  EXPENSE_UPDATED: 'expenseUpdated',
  EXPENSE_CREATED: 'expenseCreated', 
  EXPENSE_DELETED: 'expenseDeleted',
  EXPENSE_BULK_UPDATED: 'expenseBulkUpdated',
  
  ACCOUNT_UPDATED: 'accountUpdated',
  ACCOUNT_CONNECTED: 'accountConnected',
  ACCOUNT_DISCONNECTED: 'accountDisconnected',
  
  CATEGORY_UPDATED: 'categoryUpdated',
  CATEGORY_TARGET_UPDATED: 'categoryTargetUpdated',
  
  SCOPE_UPDATED: 'scopeUpdated',
  SCOPE_CHANGED: 'scopeChanged',
  
  PLAID_SYNC_COMPLETED: 'plaidSyncCompleted',
  PLAID_ITEM_UPDATED: 'plaidItemUpdated',
} as const